import { INCOTERMS, type Incoterm } from '@/constants/shipment/incoterms';
import type { DocumentTypeOption } from '@/modules/documents';
import type {
  RoadTransportCargoUnit,
  RoadTransportCommodity,
  RoadTransportPackageType,
  RoadTransportPalletType,
} from '@/store/api/endpoints/client/roadTransportApi';

export const NOT_APPLICABLE_INCOTERM = 'NotApplicable';

/** Incoterms 2020 dropped DDU, but the WebAPI `Incoterm` enum still accepts it. */
const DELIVERED_DUTY_UNPAID: Incoterm = {
  code: 'DDU',
  name: 'Delivered Duty Unpaid',
  description: 'Seller delivers goods at destination without clearing them for import',
};

const ROAD_TRANSPORT_INCOTERM_CODES = new Set([
  'EXW',
  'FCA',
  'CPT',
  'CIP',
  'DAP',
  'DPU',
  'DDP',
]);

/** Only the delivery terms that apply to road transport, in Incoterms order. */
export const ROAD_TRANSPORT_INCOTERMS: Incoterm[] = [
  ...INCOTERMS.filter((incoterm) => ROAD_TRANSPORT_INCOTERM_CODES.has(incoterm.code)),
  DELIVERED_DUTY_UNPAID,
];

/** The WebAPI `Commodity` member used when a cargo line names no commodity. */
export const NOT_APPLICABLE_COMMODITY = 'NotApplicable';

export function isNotApplicableIncoterm(incoterm: string | null): boolean {
  return incoterm === NOT_APPLICABLE_INCOTERM;
}

/** Longest incoterm location BluJay accepts; the WebAPI rejects anything longer. */
export const INCOTERM_LOCATION_MAX_LENGTH = 15;

export function requiresIncotermLocation(incoterm: string | null): boolean {
  return !!incoterm && !isNotApplicableIncoterm(incoterm);
}

export interface RoadTransportCodeOption<TCode extends string> {
  code: TCode;
  label: string;
}

/** The display label for a code, falling back to the code itself when it is not in the list. */
function resolveRoadTransportCodeLabel(
  options: readonly RoadTransportCodeOption<string>[],
  code: string,
): string {
  return options.find((option) => option.code === code)?.label ?? code;
}

/**
 * The cargo dropdowns below are constrained to the WebAPI enums (`CargoUnit`, `Commodity`).
 * Anything outside them is rejected by the create-good validator, so the wizard must not
 * offer wider lists.
 */
export const ROAD_TRANSPORT_CARGO_UNITS: RoadTransportCodeOption<RoadTransportCargoUnit>[] = [
  { code: 'KA', label: 'Carton/Box' },
  { code: 'P5', label: 'Pallet' },
  { code: 'P1', label: 'Euro Pallet' },
  { code: 'P2', label: 'Blok Pallet' },
  { code: 'CO', label: 'Piece' },
  { code: 'BB', label: 'Big Bag' },
  { code: 'BU', label: 'Bundel' },
  { code: 'DD', label: 'Dusseldorf Pallet' },
  { code: 'DR', label: 'Drum' },
  { code: 'IB', label: 'IBC' },
  { code: 'ZA', label: 'Bag' },
];

export const ROAD_TRANSPORT_PALLET_TYPES: RoadTransportCodeOption<RoadTransportPalletType>[] = [
  { code: 'DusseldorfPallet', label: 'Düsseldorf Pallet' },
  { code: 'Pallet', label: 'Pallet' },
  { code: 'EuroPallet', label: 'Europallet' },
  { code: 'BlockPallet', label: 'Block Pallet' },
  { code: 'BigBag', label: 'Big Bag' },
  { code: 'IntermediateBulkContainer', label: 'IBC' },
];

export const ROAD_TRANSPORT_PACKAGE_TYPES: RoadTransportCodeOption<RoadTransportPackageType>[] = [
  { code: 'Piece', label: 'Piece' },
  { code: 'CartonBox', label: 'Carton/Box' },
  { code: 'Bag', label: 'Bag' },
  { code: 'Drum', label: 'Drum' },
  { code: 'Bundle', label: 'Bundle' },
];

/** The TMS unit code the WebAPI stores for each pallet type (`RoadTransportCargoTypeExtensions`). */
export const ROAD_TRANSPORT_PALLET_TYPE_BY_UNIT: Partial<
  Record<RoadTransportCargoUnit, RoadTransportPalletType>
> = {
  DD: 'DusseldorfPallet',
  P5: 'Pallet',
  P1: 'EuroPallet',
  P2: 'BlockPallet',
  BB: 'BigBag',
  IB: 'IntermediateBulkContainer',
};

/** The TMS unit code the WebAPI stores for each package type (`RoadTransportCargoTypeExtensions`). */
export const ROAD_TRANSPORT_PACKAGE_TYPE_BY_UNIT: Partial<
  Record<RoadTransportCargoUnit, RoadTransportPackageType>
> = {
  CO: 'Piece',
  KA: 'CartonBox',
  ZA: 'Bag',
  DR: 'Drum',
  BU: 'Bundle',
};

export function resolveRoadTransportPalletType(code: string): string {
  return resolveRoadTransportCodeLabel(ROAD_TRANSPORT_PALLET_TYPES, code);
}

export function resolveRoadTransportPackageType(code: string): string {
  return resolveRoadTransportCodeLabel(ROAD_TRANSPORT_PACKAGE_TYPES, code);
}

/**
 * Shown as the bare enum codes, matching how the TMS names them elsewhere. Unlike units there
 * is no separate display label — the code is the label.
 */
export const ROAD_TRANSPORT_COMMODITIES: RoadTransportCommodity[] = ['ADR', 'FOOD', 'OTHER'];

/** The commodity that makes a cargo line dangerous goods, and so demands an MSDS document. */
export const ADR_COMMODITY: RoadTransportCommodity = 'ADR';

export function isAdrCommodity(commodity: string): boolean {
  return commodity === ADR_COMMODITY;
}

export function resolveRoadTransportCargoUnit(code: string): string {
  return resolveRoadTransportCodeLabel(ROAD_TRANSPORT_CARGO_UNITS, code);
}

/** Units whose quantity is a pallet count (not cartons, pieces, drums, …). */
const ROAD_TRANSPORT_PALLET_UNITS = new Set<RoadTransportCargoUnit>(['P1', 'P2', 'P5', 'DD']);

export function isRoadTransportPalletUnit(unit: string): boolean {
  return ROAD_TRANSPORT_PALLET_UNITS.has(unit as RoadTransportCargoUnit);
}

export const MSDS_DOCUMENT_TYPE = 'Msds';

/**
 * Upload dropdown types for road transport — mirrors WebAPI / BabelWay DocumentType labels.
 * Values are the enum names the upload endpoint accepts. MSDS leads the list: it is the one
 * type the flow can demand, so it is the one people come to this dropdown looking for.
 */
export const ROAD_TRANSPORT_DOCUMENT_TYPE_OPTIONS: DocumentTypeOption[] = [
  { value: MSDS_DOCUMENT_TYPE, label: 'MSDS' },
  { value: 'Ams', label: 'AMS' },
  { value: 'BookingConfirmation', label: 'Booking Confirmation' },
  { value: 'Certificate', label: 'Certificate' },
  { value: 'CertificateOfOrigin', label: 'Certificate of Origin' },
  { value: 'CommercialInvoice', label: 'Commercial Invoice' },
  { value: 'DangerousGoodsDeclaration', label: 'Dangerous Goods Declaration' },
  { value: 'DebitNote', label: 'Debit Note' },
  { value: 'DelayNotice', label: 'Delay Notice (Official statement)' },
  { value: 'Ens', label: 'ENS' },
  { value: 'FlegtLicense', label: 'FLEGT License' },
  { value: 'HouseBillOfLading', label: 'House Bill of Lading' },
  { value: 'Isf', label: 'ISF' },
  { value: 'Manifest', label: 'Manifest' },
  { value: 'MasterBillOfLading', label: 'Master Bill of Lading' },
  { value: 'OtherSupporting', label: 'Other Supporting' },
  { value: 'PicturePhoto', label: 'Picture/Photo' },
  { value: 'TelexRelease', label: 'Telex Release' },
  { value: 'PackingList', label: 'Packing List' },
];
